/**
 * Project funding orchestration: create a funding session, open checkout when
 * the provider needs one, verify, and report the outcome.
 *
 * Ported from `handleFundProject` in `../pages/project-detail.tsx`, which is
 * where the sequence below comes from. Two calls create a session:
 * `fundProject` for milestones and `topUpEscrow` for money no milestone asks
 * for. Either only creates a provider order and `pending` escrow rows; the
 * money moves when `verifyProjectFunding` (or the Cashfree webhook) settles
 * them. Both run through the one sequence below, so the handshake exists once.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	cashfreeEnvironmentMismatch,
	launchCashfreeCheckout,
} from "../services/cashfree-checkout";
import {
	type FundingSession,
	fundProject,
	topUpEscrow,
	verifyProjectFunding,
} from "../services/projects-api.service";

/**
 * The branch the orchestration ended on. `"funded"` is the only value that
 * means the server confirmed the payment.
 */
export type ProjectFundingOutcome =
	| "funded"
	| "pending-confirmation"
	| "verification-failed"
	| "cancelled"
	| "checkout-unavailable"
	| "environment-mismatch"
	| "fund-failed"
	| "checkout-error"
	| "refresh-failed"
	| "in-flight";

export interface FundMilestonesArgs {
	projectId: string;
	/** Omit or pass an empty array to fund every unfunded milestone. */
	milestoneIds?: string[];
	/**
	 * Run after the server confirms the payment — the caller's refetch. It is
	 * awaited inside the orchestration's try block, so a throw from here is
	 * reported as a failed refresh of a paid project, not a failed checkout.
	 */
	onFunded?: () => void | Promise<void>;
}

export interface AddFundsArgs {
	projectId: string;
	/** Whole rupees. */
	amount: number;
	/** As `FundMilestonesArgs.onFunded`. */
	onFunded?: () => void | Promise<void>;
}

export interface UseProjectFundingResult {
	/** True from the first call until its outcome is known. */
	isFunding: boolean;
	fundMilestones: (args: FundMilestonesArgs) => Promise<ProjectFundingOutcome>;
	/** Top up the project's escrow by `amount`. */
	addFunds: (args: AddFundsArgs) => Promise<ProjectFundingOutcome>;
}

/** One funding attempt: the call that creates its session, and its wording. */
interface FundingAttempt {
	projectId: string;
	createSession: () => Promise<{
		success: boolean;
		fundingSession?: FundingSession;
		error?: string;
	}>;
	onFunded?: () => void | Promise<void>;
	/** Toasted when the server confirms the payment. */
	fundedMessage: string;
	/** Toasted when `createSession` fails and gives no reason of its own. */
	createFailedMessage: string;
}

/**
 * Every branch that reaches the server raises a toast, so a caller needs no
 * error handling of its own; the returned outcome is for callers that also drive
 * UI state. `"in-flight"` is the one silent outcome — see the lock below for why.
 */
export function useProjectFunding(): UseProjectFundingResult {
	const { t } = useTranslation();
	const [isFunding, setIsFunding] = React.useState(false);
	// A ref, not the state above: it is assigned before the first await, so a
	// second click in the same tick sees it (R6). State would still be false.
	const inFlight = React.useRef(false);

	const runFunding = React.useCallback(
		async ({
			projectId,
			createSession,
			onFunded,
			fundedMessage,
			createFailedMessage,
		}: FundingAttempt): Promise<ProjectFundingOutcome> => {
			// The one outcome here that raises no toast, and deliberately so. R2
			// ("every failure is visible. Nothing is swallowed.") enumerates the
			// failures of a funding attempt: a cancelled checkout, an unavailable
			// session, a failed verification and a pending one. A deduplicated click is
			// none of those. It is the second of two clicks on one intent, and the
			// attempt it duplicates is still running and will toast its own outcome —
			// so nothing is swallowed by saying nothing here, while a message would be
			// a second notification about one action, whose likeliest reading ("your
			// payment was refused") is false.
			//
			// The homeowner buttons that start one (`ho-costs-tab.tsx`,
			// `ho-milestones-tab.tsx`, and Add funds in `escrow-cover-panel.tsx`) pass
			// `disabled={isFunding}` to `Btn`, which spreads it onto a real `<button>`,
			// so the window this closes is only between the click and React committing
			// that state. No caller reads the returned outcome, which is right for
			// this one: there is nothing for them to do about it.
			if (inFlight.current) return "in-flight";
			inFlight.current = true;
			setIsFunding(true);

			let fundingVerified = false;
			try {
				const result = await createSession();

				// A response without a funding session is a failure: there is no order
				// to take to checkout and none to verify, so nothing would be charged.
				if (!result.success || !result.fundingSession) {
					toast.error(result.error || createFailedMessage);
					return "fund-failed";
				}

				const { fundingSession } = result;

				// A resumed session is one the server found already `pending` rather
				// than created, and it may already have been paid — a lost verify
				// response is exactly what sends a homeowner back to press the same
				// button again.
				// `verifyProjectFunding` settles such an order and returns verified.
				// When it has not been paid it returns not-verified, and the cost is
				// one request before the checkout opens as usual.
				if (fundingSession.resumed) {
					const settled = await verifyProjectFunding(
						projectId,
						fundingSession.providerOrderId,
					);
					if (settled.success && settled.verified) {
						fundingVerified = true;
						toast.success(fundedMessage);
						await onFunded?.();
						return "funded";
					}
					// Falling through to the checkout is right for exactly one of the two
					// remaining shapes. `success && !verified` is the server having looked
					// at the order and found it unpaid, which is what a checkout is for.
					// `!success` is the verify itself having failed, which establishes
					// nothing about the order: it may already have been paid. Opening a
					// checkout on it would ask the homeowner to pay an order that might
					// already be collected, and would drop the reason the server gave —
					// the same reason the non-resumed branch below reports verbatim.
					if (!settled.success) {
						toast.error(
							settled.error || t("project.payments.verificationFailed"),
						);
						return "verification-failed";
					}
				}

				if (fundingSession.provider === "cashfree") {
					// A build whose `VITE_CASHFREE_MODE` disagrees with the backend's
					// Cashfree environment cannot complete a checkout: the session was
					// minted for the other environment's checkout. Refuse with the reason
					// rather than opening an SDK that fails for the homeowner.
					const environmentMismatch = cashfreeEnvironmentMismatch(
						fundingSession.environment,
					);
					if (environmentMismatch) {
						toast.error(environmentMismatch);
						return "environment-mismatch";
					}

					if (!fundingSession.paymentSessionId) {
						toast.error(t("project.payments.checkoutUnavailable"));
						return "checkout-unavailable";
					}

					const checkoutResult = await launchCashfreeCheckout(
						fundingSession.paymentSessionId,
					);

					// The homeowner closed the modal without paying, so there is nothing
					// for verification to settle.
					if (checkoutResult.status === "cancelled") {
						toast.error(t("project.payments.paymentNotCompleted"));
						return "cancelled";
					}
				}

				const verification = await verifyProjectFunding(
					projectId,
					fundingSession.providerOrderId,
				);

				if (verification.success && verification.verified) {
					fundingVerified = true;
					toast.success(fundedMessage);
					await onFunded?.();
					return "funded";
				}

				if (verification.success) {
					toast.error(t("project.payments.paymentPendingConfirmation"));
					return "pending-confirmation";
				}

				toast.error(
					verification.error || t("project.payments.verificationFailed"),
				);
				return "verification-failed";
			} catch {
				// Past `fundingVerified = true` the server has already confirmed the
				// payment and the only thing left is `onFunded`. Reporting an
				// interrupted checkout here would tell the homeowner to pay again.
				if (fundingVerified) {
					toast.error(t("project.payments.fundingRefreshFailed"));
					return "refresh-failed";
				}
				toast.error(t("project.payments.checkoutFailed"));
				return "checkout-error";
			} finally {
				inFlight.current = false;
				setIsFunding(false);
			}
		},
		[t],
	);

	const fundMilestones = React.useCallback(
		({
			projectId,
			milestoneIds,
			onFunded,
		}: FundMilestonesArgs): Promise<ProjectFundingOutcome> =>
			runFunding({
				projectId,
				createSession: () =>
					fundProject(
						projectId,
						milestoneIds && milestoneIds.length > 0 ? milestoneIds : undefined,
					),
				onFunded,
				fundedMessage: t("project.payments.statusFunded"),
				createFailedMessage: "Failed to fund project",
			}),
		[runFunding, t],
	);

	const addFunds = React.useCallback(
		({ projectId, amount, onFunded }: AddFundsArgs) =>
			runFunding({
				projectId,
				createSession: () => topUpEscrow(projectId, amount),
				onFunded,
				fundedMessage: t("project.payments.fundsAdded"),
				createFailedMessage: "Failed to add funds",
			}),
		[runFunding, t],
	);

	return { isFunding, fundMilestones, addFunds };
}
